import { useCallback, useDeferredValue, useEffect, useMemo, useState, useTransition } from 'react';
import type { Key } from 'react-aria-components';
import type { UseFormReturn } from 'react-hook-form';
import { useFormState, useWatch } from 'react-hook-form';
import { useDebouncedValue } from '@gonfalon/async/useDebouncedValue';
import { relativeDateString } from '@gonfalon/datetime/relativeDateString';
import { HelpTooltip } from '@gonfalon/launchpad-experimental/HelpTooltip';
import { trackMetricsEvent } from '@gonfalon/metrics/analytics';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import { useEventActivityByProjModern } from '@gonfalon/rest-api/hooks/useEventActivityByProj';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { ComboBox, FieldGroup, Group, Input, Popover, ProgressBar } from '@launchpad-ui/components';
import { clsx } from 'clsx';

import { RequiredLabel } from '../components/RequiredLabel';
import type { EventListBoxItemProps, EventOption } from '../EventActivitySearchResults/EventActivitySearchResults';
import {
  EventActivitySearchResults,
  SEARCH_RESULT_DISPLAY_NUMBER,
} from '../EventActivitySearchResults/EventActivitySearchResults';
import type { MetricForm } from '../formUtils';

import { FieldConstants } from './FieldConstants';
import { loaderData } from './loaderData';

import styles from './EventActivityFormField.module.css';

interface EventActivityFormFieldProps {
  context: UseFormReturn<MetricForm>;
  /**
   * Form field path for the event key.
   * Defaults to 'eventKey' (simple metric / ratio numerator).
   * Pass 'denominator.eventKey' for the ratio denominator term.
   */
  eventKeyPath?: 'eventKey' | 'denominator.eventKey';
  /**
   * Pre-resolved data source key for event filtering.
   * When omitted the component watches the 'dataSourceKey' form field directly.
   * Pass this when the effective key involves fallback logic (e.g. ratio denominator
   * falling back to the numerator's data source).
   */
  resolvedDataSourceKey?: string;
  /**
   * Extra class applied to both the ComboBox container and the Input so callers
   * can override the default fixed width (e.g. pass a `width: 100%` utility class
   * when the field is rendered inside a full-width form layout).
   */
  inputClassName?: string;
}

export const EventActivityFormField: React.FC<EventActivityFormFieldProps> = ({
  context,
  eventKeyPath = 'eventKey',
  resolvedDataSourceKey,
  inputClassName,
}: EventActivityFormFieldProps) => {
  const projectKey = useProjectKey();

  const { control, getFieldState, register, setValue } = context;
  const [loading, setLoading] = useState(false);
  const [isPending, startTransition] = useTransition();

  const eventKeyFormValue = useWatch({ control, name: eventKeyPath }) ?? '';
  const eventKeyFormState = useFormState({ control, name: eventKeyPath });

  const watchedDataSourceKey = useWatch({ control, name: 'dataSourceKey' });
  const dataSourceKeyFormValue = resolvedDataSourceKey ?? watchedDataSourceKey;

  const eventKeyError = getFieldState(eventKeyPath, eventKeyFormState).error?.message;
  const deferredEventKey = useDeferredValue(useDebouncedValue(eventKeyFormValue, 200));

  const {
    data: eventData,
    isLoading,
    isFetching,
    isError,
  } = useEventActivityByProjModern(
    {
      projectKey,
      query: {
        q: deferredEventKey,
        limit: SEARCH_RESULT_DISPLAY_NUMBER,
        datasource: dataSourceKeyFormValue ?? 'launchdarkly-hosted',
      },
    },
    {
      enabled: !eventKeyError,
      placeholderData: (prev) => prev,
    },
  );

  useEffect(() => {
    if (loading && !isFetching) {
      setLoading(false);
    }
  }, [isFetching, loading]);

  const getAutofillPercentage = useCallback((autofillSelection: string, currentEventKey: string) => {
    const autofillSelectionLength = Math.max(autofillSelection.length, 1);
    const currentEventKeyLength = currentEventKey.length;
    return Math.floor((currentEventKeyLength / autofillSelectionLength) * 100);
  }, []);

  // The key here is now the index of the search result found in
  // the EventActivitySearchResults
  const handleSelectEventKey = useCallback(
    (index: Key | null): void => {
      // The last two checks in this if statement are mostly for typescript
      if (typeof index !== 'number' || !eventData?.items?.length || !eventData.items[index]) {
        return;
      }
      const { eventKey } = eventData.items[index];
      const autofillPercentage = getAutofillPercentage(eventKey, eventKeyFormValue);
      trackMetricsEvent('Event Key Autofilled - Percentage', {
        metricValue: autofillPercentage,
        value: autofillPercentage,
      });

      trackMetricsEvent('Event Key Autofilled - Suggestion Rank', {
        metricValue: index + 1,
        value: index + 1,
      });
      setValue(eventKeyPath, eventKey, { shouldValidate: true, shouldDirty: true });
    },
    [eventData, setValue, getAutofillPercentage, eventKeyFormValue, eventKeyPath],
  );

  const options: EventOption[] = useMemo(
    () =>
      (eventData?.items ?? [])
        .map((item, index) => ({
          id: item.eventKey,
          index,
          name: item.eventKey,
          description: `Last seen: ${relativeDateString(item.lastSeen)}`,
          event: item,
          dataSource: item.dataSource,
        }))
        // Filter out events that are not CUSTOM kind - other kinds refer to events whose Keys
        // are generated post metric creation and by the system (e.g. pageview, click)
        // TODO move this filter to the backend
        .filter((item) => item.event.eventKind === 'custom')
        // Filter out events that aren't from the selected data source
        .filter((item) => {
          if (!dataSourceKeyFormValue) {
            return true;
          } else {
            return item.dataSource?.key === dataSourceKeyFormValue;
          }
        })
        .slice(0, SEARCH_RESULT_DISPLAY_NUMBER),
    [eventData?.items, dataSourceKeyFormValue],
  );

  const numberOfVisibleResults = useMemo(
    () => Math.min(options.length, SEARCH_RESULT_DISPLAY_NUMBER),
    [options.length],
  );

  const sectioned = useMemo(() => {
    if (isLoading) {
      return [{ name: 'Loading Recent Events', children: loaderData as EventListBoxItemProps }];
    }
    if (isError || !options?.length) {
      return [];
    }
    return [
      {
        name: `Showing the most relevant ${numberOfVisibleResults} results`,
        children: options as EventListBoxItemProps,
      },
    ];
  }, [isError, isLoading, numberOfVisibleResults, options]);

  const setLoadingState = useCallback(() => {
    startTransition(() => {
      setLoading(true);
    });
  }, []);

  return (
    <FieldGroup errorMessage={eventKeyError}>
      <ComboBox
        className={clsx(styles.eventActivityContainer, styles.eventActivityContainerNewWidth, inputClassName)}
        allowsEmptyCollection
        allowsCustomValue
        menuTrigger="focus"
        onSelectionChange={handleSelectEventKey}
        defaultFilter={() => true}
        items={sectioned}
      >
        <span className={styles.tooltipContainer}>
          {/*
           * Do not pass `htmlFor` here. RAC 1.17's ComboBox supplies the label/input
           * association via LabelContext; an explicit `htmlFor` would override the
           * context-provided id and break the combobox accessible name (the Input's id
           * is auto-generated by RAC, not `FieldConstants.key`).
           */}
          <RequiredLabel label={FieldConstants.label} />
          <HelpTooltip placement="right">
            {isLDHosted(dataSourceKeyFormValue) ? FieldConstants.toolTipText : FieldConstants.warehouseToolTipText}
          </HelpTooltip>
        </span>
        <Group>
          <Input
            {...register(eventKeyPath, {
              ...FieldConstants.registerOptions,
              onChange: setLoadingState,
              shouldUnregister: true,
            })}
            value={eventKeyFormValue}
            placeholder={FieldConstants.placeholder}
            className={clsx(styles.eventKeyInput, inputClassName)}
            data-test-id="metric-event-key-textfield"
          />
          {(isLoading || loading || isPending) && (
            <ProgressBar size="small" isIndeterminate aria-label="Loading recent Event Keys" />
          )}
        </Group>
        <Popover>
          <EventActivitySearchResults isError={isError} />
        </Popover>
      </ComboBox>
    </FieldGroup>
  );
};
